import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Booth,
  lockBooth,
  requestOffline48hBoothHold,
  createOrder,
  verifyBoothPayment,
  loadRazorpayScript,
  ApiError,
  CreateOrderResponse,
} from '../lib/api';
import {
  X,
  Lock,
  Clock,
  ShieldCheck,
  AlertCircle,
  Building2,
  Mail,
  CreditCard,
  Check,
  User,
  Phone,
  FileText,
  Landmark,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { TurnstileWidget } from './TurnstileWidget';

export interface BoothModalProps {
  booth: Booth | null;
  isOpen: boolean;
  onClose: () => void;
  onLocked?: (updatedBooth: Booth) => void;
  onOrderSuccess?: (order: CreateOrderResponse) => void;
}

export const BoothModal: React.FC<BoothModalProps> = ({
  booth,
  isOpen,
  onClose,
  onLocked,
  onOrderSuccess,
}) => {
  const queryClient = useQueryClient();
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [gstin, setGstin] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLockedByMe, setIsLockedByMe] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
  const [currentBooth, setCurrentBooth] = useState<Booth | null>(booth);
  const [holdToken, setHoldToken] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [isOfflineHoldSuccess, setIsOfflineHoldSuccess] = useState(false);
  const [offlineHoldExpiresAt, setOfflineHoldExpiresAt] = useState<string | null>(null);
  /** Payment went through at Razorpay but our server couldn't confirm yet. */
  const [isPaymentPending, setIsPaymentPending] = useState(false);
  const [verifiedPortalToken, setVerifiedPortalToken] = useState<string | null>(null);

  useEffect(() => {
    setCurrentBooth(booth);
    setErrorMessage(null);
    setIsPaymentSuccess(false);
    setIsOfflineHoldSuccess(false);
    setOfflineHoldExpiresAt(null);
    setIsPaymentPending(false);
    setIsConfirming(false);
    setIsPaymentLoading(false);
    setTurnstileToken(null);
    setVerifiedPortalToken(null);

    // Only treat the hold as "mine" if this browser actually holds the hold token.
    const myHold = booth?.holdToken || null;
    if (booth?.status === 'on_hold' && booth.lockedUntil && myHold) {
      const remaining = Math.max(0, Math.floor((new Date(booth.lockedUntil).getTime() - Date.now()) / 1000));
      if (remaining > 0) {
        setSecondsRemaining(remaining);
        setIsLockedByMe(true);
      } else {
        setIsLockedByMe(false);
        setSecondsRemaining(0);
      }
    } else {
      setIsLockedByMe(false);
      setSecondsRemaining(0);
    }
  }, [booth]);

  // 15-Minute Countdown timer
  useEffect(() => {
    if (!isLockedByMe || secondsRemaining <= 0) return;

    const interval = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          setIsLockedByMe(false);
          clearInterval(interval);
          queryClient.invalidateQueries({ queryKey: ['booths'] });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isLockedByMe, secondsRemaining, queryClient]);

  const lockMutation = useMutation({
    mutationFn: async () => {
      if (!currentBooth) throw new Error('No booth selected');
      return lockBooth(currentBooth.id, {
        companyEmail,
        companyName: companyName.trim() || undefined,
        turnstileToken: turnstileToken || undefined,
      });
    },
    onSuccess: (data) => {
      setErrorMessage(null);
      setIsLockedByMe(true);
      setSecondsRemaining(15 * 60); // 15 minutes
      if (data.holdToken || data.booth?.holdToken) {
        setHoldToken(data.holdToken || data.booth?.holdToken || null);
      }

      const updated: Booth = {
        ...(currentBooth!),
        status: 'on_hold',
        lockedUntil: data.booth?.lockedUntil || new Date(Date.now() + 15 * 60 * 1000).toISOString(),
        companyName: companyName || null,
        holdToken: data.holdToken || data.booth?.holdToken || null,
      };

      setCurrentBooth(updated);
      queryClient.invalidateQueries({ queryKey: ['booths'] });

      try {
        confetti({
          particleCount: 40,
          spread: 50,
          origin: { y: 0.7 },
        });
      } catch {
        // Confetti optional
      }

      if (onLocked) {
        onLocked(updated);
      }
    },
    onError: (err: any) => {
      if (err instanceof ApiError && err.status === 409) {
        setErrorMessage(err.message || 'Stall was just claimed by another exhibitor.');
      } else if (err?.message) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Failed to reserve stall. Please try again.');
      }
      queryClient.invalidateQueries({ queryKey: ['booths'] });
    },
  });

  const offlineHoldMutation = useMutation({
    mutationFn: async () => {
      if (!currentBooth) throw new Error('No booth selected');
      return requestOffline48hBoothHold(currentBooth.id, {
        companyEmail: companyEmail.trim(),
        companyName: companyName.trim(),
        contactPerson: contactPerson.trim(),
        contactPhone: contactPhone.trim(),
        gstin: gstin.trim() ? gstin.trim().toUpperCase() : undefined,
        holdToken: holdToken || currentBooth.holdToken || undefined,
        turnstileToken: turnstileToken || undefined,
      });
    },
    onSuccess: (data) => {
      setErrorMessage(null);
      setVerifiedPortalToken(data.portalToken || null);
      setOfflineHoldExpiresAt(data.holdExpiresAt || null);
      setIsOfflineHoldSuccess(true);
      setIsLockedByMe(false);

      const updated: Booth = {
        ...(currentBooth!),
        status: 'on_hold',
        lockedUntil: data.holdExpiresAt,
        companyName: companyName.trim() || null,
      };
      setCurrentBooth(updated);
      queryClient.invalidateQueries({ queryKey: ['booths'] });

      try {
        confetti({
          particleCount: 60,
          spread: 60,
          origin: { y: 0.65 },
        });
      } catch {
        // Confetti optional
      }

      if (onLocked) {
        onLocked(updated);
      }
    },
    onError: (err: any) => {
      setErrorMessage(err?.message || 'Failed to place 48-hour offline NEFT/RTGS hold.');
      queryClient.invalidateQueries({ queryKey: ['booths'] });
    },
  });

  const validateFormFields = (): boolean => {
    if (!companyEmail || !companyName.trim() || !contactPerson.trim() || !contactPhone.trim()) {
      setErrorMessage('Please fill in company email, company name, contact person and phone.');
      return false;
    }
    if (gstin.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin.trim().toUpperCase())) {
      setErrorMessage('GSTIN looks invalid. It should be 15 characters, e.g. 27AAACM1234A1Z5.');
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const handleReserve = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateFormFields()) return;
    lockMutation.mutate();
  };

  const handleRequestOffline48hHold = () => {
    if (!validateFormFields()) return;
    offlineHoldMutation.mutate();
  };

  // Payment Handler invoking Razorpay Checkout
  const handleProceedToPayment = async () => {
    if (!currentBooth) return;
    const activeHold = holdToken || currentBooth.holdToken;
    if (!activeHold) {
      setErrorMessage('Stall hold reservation has expired or is missing. Please hold the stall first.');
      setIsPaymentLoading(false);
      return;
    }

    setIsPaymentLoading(true);
    try {
      // 1. Invoke /api/orders/create with holdToken + real contact details
      const orderData = await createOrder({
        boothId: currentBooth.id,
        holdToken: activeHold,
        companyEmail: companyEmail.trim(),
        companyName: companyName.trim(),
        contactPerson: contactPerson.trim(),
        contactPhone: contactPhone.trim(),
        gstin: gstin.trim() ? gstin.trim().toUpperCase() : undefined,
      });

      // 2. Load dynamic Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay payment SDK.');
      }

      // 3. Launch Razorpay Checkout
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: 'IIAB 2027',
        description: `Stall ${currentBooth.id} (${currentBooth.dimensions}) Registration`,
        order_id: orderData.razorpayOrderId || orderData.orderId,
        prefill: {
          email: companyEmail,
          name: contactPerson,
          contact: contactPhone,
        },
        theme: {
          color: '#10b981',
        },
        handler: async function (response: any) {
          // Razorpay says the payment went through. Ask OUR server to verify
          // Razorpay's signature before we show anything as booked.
          setIsConfirming(true);
          try {
            const verifyRes = await verifyBoothPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });

            setVerifiedPortalToken(
              (verifyRes as any)?.portalToken || (orderData as any)?.portalToken || null
            );
            setIsPaymentSuccess(true);
            setCurrentBooth((prev) => (prev ? { ...prev, status: 'booked', lockedUntil: null } : null));
            try {
              confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
            } catch {
              // Confetti optional
            }
            if (onOrderSuccess) {
              onOrderSuccess(orderData);
            }
          } catch (err: any) {
            if (err instanceof ApiError && err.status === 409) {
              // Someone else's payment confirmed first - server has triggered a refund.
              setErrorMessage(err.message);
            } else {
              // Payment captured but confirmation failed (network etc.). The Razorpay
              // webhook will still confirm it server-side; tell the user honestly.
              setIsPaymentPending(true);
            }
          } finally {
            setIsConfirming(false);
            setIsPaymentLoading(false);
            queryClient.invalidateQueries({ queryKey: ['booths'] });
          }
        },
        modal: {
          ondismiss: function () {
            setIsPaymentLoading(false);
          },
        },
      };

      if (typeof window !== 'undefined' && (window as any).Razorpay) {
        const rzp = new (window as any).Razorpay(options);
        rzp.on('payment.failed', function (resp: any) {
          setErrorMessage(`Payment declined: ${resp.error?.description || 'Transaction failed'}`);
          setIsPaymentLoading(false);
        });
        rzp.open();
      } else {
        throw new Error('Razorpay SDK not initialized.');
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to initialize payment checkout.');
      setIsPaymentLoading(false);
    }
  };

  const formatTimer = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen || !currentBooth) return null;

  const isAvailable = currentBooth.status === 'available';
  const isOnHold = currentBooth.status === 'on_hold';
  const isBooked = currentBooth.status === 'booked';

  const basePrice = Number(currentBooth.priceInr);
  const gstAmount = Math.round(basePrice * 0.18);
  const totalPayable = basePrice + gstAmount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/40 backdrop-blur-md animate-in fade-in duration-200">
      <div
        className="relative w-full max-w-lg bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between pb-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-[#0A3D27] font-bold text-lg shadow-2xs">
              {currentBooth.id}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">
                  Stall {currentBooth.id}
                </h3>
                <span
                  className={`text-xs px-2.5 py-0.5 rounded-full font-semibold border ${
                    isPaymentSuccess || isBooked
                      ? 'bg-rose-50 text-rose-800 border-rose-200'
                      : isOnHold
                      ? 'bg-amber-50 text-amber-800 border-amber-200'
                      : 'bg-emerald-50 text-[#0A3D27] border-emerald-200'
                  }`}
                >
                  {isPaymentSuccess || isBooked ? 'Booked' : isOnHold ? 'On Hold' : 'Available'}
                </span>
              </div>
              <p className="text-xs text-slate-500">Pavilion 3 Prime Exhibition Space</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Booth Specifications Grid */}
        <div className="grid grid-cols-3 gap-3 mt-5 mb-4">
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl">
            <span className="text-xs text-slate-500 block font-medium">Dimensions</span>
            <span className="text-sm sm:text-base font-bold text-slate-900 mt-0.5 block">{currentBooth.dimensions}</span>
          </div>
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl">
            <span className="text-xs text-slate-500 block font-medium">Area</span>
            <span className="text-sm sm:text-base font-bold text-slate-900 mt-0.5 block">{currentBooth.areaSqm} sqm</span>
          </div>
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl">
            <span className="text-xs text-[#0A3D27] block font-medium">Base Price</span>
            <span className="text-sm sm:text-base font-bold text-[#0A3D27] mt-0.5 block">
              ₹{basePrice.toLocaleString('en-IN')}
            </span>
          </div>
        </div>

        {/* Clear 3-Line 18% GST Tax Breakdown (SAC 998596) */}
        <div className="mb-5 p-3.5 rounded-2xl bg-slate-50 border border-slate-200/90 space-y-1.5 text-xs">
          <div className="flex justify-between text-slate-600">
            <span>Base Stall Price ({currentBooth.areaSqm} sqm):</span>
            <span className="font-mono font-semibold text-slate-900">
              ₹{basePrice.toLocaleString('en-IN')}
            </span>
          </div>
          <div className="flex justify-between text-slate-600">
            <span>+ 18% GST (SAC 998596 · Exhibition Space):</span>
            <span className="font-mono font-semibold text-slate-900">
              ₹{gstAmount.toLocaleString('en-IN')}
            </span>
          </div>
          <div className="flex justify-between pt-1.5 border-t border-slate-200 font-bold text-slate-900 text-sm">
            <span>Total Payable:</span>
            <span className="font-mono text-[#0A3D27]">
              ₹{totalPayable.toLocaleString('en-IN')}
            </span>
          </div>
        </div>

        {/* Error Alert on 409 Conflict or errors */}
        {errorMessage && (
          <div className="mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-200 flex items-start gap-3 text-rose-800 text-sm animate-in fade-in duration-200">
            <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1 font-medium">{errorMessage}</div>
          </div>
        )}

        {/* Confirming / pending / success screens */}
        {isConfirming ? (
          <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl text-center space-y-3" id="booth-payment-confirming">
            <div className="w-10 h-10 border-4 border-[#0A3D27] border-t-transparent rounded-full animate-spin mx-auto" />
            <h4 className="text-base font-bold text-slate-900">Confirming your payment…</h4>
            <p className="text-xs text-slate-600 max-w-sm mx-auto">
              Please don't close this window. We're verifying your payment with Razorpay.
            </p>
          </div>
        ) : isPaymentPending ? (
          <div className="p-6 bg-amber-50 border border-amber-200 rounded-2xl text-center space-y-3" id="booth-payment-pending">
            <Clock className="w-8 h-8 text-amber-600 mx-auto" />
            <h4 className="text-base font-bold text-slate-900">Payment received – confirmation in progress</h4>
            <p className="text-xs text-slate-600 max-w-sm mx-auto">
              Your payment went through, but we couldn't confirm Stall {currentBooth.id} instantly. You'll get a
              confirmation email at <strong>{companyEmail}</strong> within a few minutes. Please don't pay again.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 py-2.5 px-6 bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold rounded-xl transition-all cursor-pointer"
            >
              OK
            </button>
          </div>
        ) : isOfflineHoldSuccess ? (
          <div className="p-6 bg-[#FAF5EA] border border-[#E6C687] rounded-2xl text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-amber-100 border border-[#E6C687] text-[#785613] flex items-center justify-center mx-auto">
              <Clock className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <h4 className="text-lg font-bold text-slate-900">
                48-Hour Offline NEFT / RTGS Hold Confirmed!
              </h4>
              <p className="text-xs text-slate-600 max-w-sm mx-auto">
                Stall <strong>{currentBooth.id}</strong> is held for{' '}
                <strong>{companyName || 'your organization'}</strong> for 48 hours
                {offlineHoldExpiresAt
                  ? ` (until ${new Date(offlineHoldExpiresAt).toLocaleString('en-IN', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })})`
                  : ''}
                .
              </p>
            </div>

            <div className="p-3.5 rounded-xl bg-white border border-[#E6C687]/80 text-[11px] text-slate-700 text-left space-y-1.5">
              <div className="font-bold text-[#785613]">
                🏦 Offline Bank Transfer &amp; Exhibitor Portal Access
              </div>
              <p>
                Total Payable via NEFT/RTGS: <strong>₹{totalPayable.toLocaleString('en-IN')}</strong> (incl. 18% GST).
                Your Exhibitor Portal link has been dispatched to <strong>{companyEmail}</strong>. Once our Finance
                team verifies your UTR reference, your stall will transition to <strong>Booked</strong> and your
                representative passes will unlock automatically.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 pt-1">
              <a
                href={
                  verifiedPortalToken
                    ? `/exhibitor-portal?token=${encodeURIComponent(verifiedPortalToken)}`
                    : '/exhibitor-portal'
                }
                className="w-full sm:w-auto py-2.5 px-5 bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-emerald-950/20"
              >
                Open Exhibitor Portal →
              </a>
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto py-2.5 px-5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-bold rounded-xl transition-all cursor-pointer"
              >
                Done
              </button>
            </div>
          </div>
        ) : isPaymentSuccess ? (
          <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 border border-emerald-300 text-[#0A3D27] flex items-center justify-center mx-auto">
              <Check className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <h4 className="text-lg font-bold text-slate-900">Stall {currentBooth.id} Confirmed &amp; Booked!</h4>
              <p className="text-xs text-slate-600 max-w-sm mx-auto">
                Payment of <strong>₹{totalPayable.toLocaleString('en-IN')}</strong> (incl. 18% GST) verified. Stall{' '}
                {currentBooth.id} is officially reserved for <strong>{companyName || 'your organization'}</strong>.
              </p>
            </div>

            <div className="p-3 rounded-xl bg-white border border-emerald-200/80 text-[11px] text-slate-700 text-left space-y-1">
              <div className="font-bold text-[#0A3D27]">✅ GST Tax Invoice &amp; Exhibitor Portal Dispatched</div>
              <p>
                Your official GST-compliant tax invoice (SAC 998596) via Zoho Books and your Exhibitor Portal magic link
                are being emailed to <strong>{companyEmail}</strong>.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 pt-1">
              <a
                href={
                  verifiedPortalToken
                    ? `/exhibitor-portal?token=${encodeURIComponent(verifiedPortalToken)}`
                    : '/exhibitor-portal'
                }
                className="w-full sm:w-auto py-2.5 px-5 bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-emerald-950/20"
              >
                Open Exhibitor Portal →
              </a>
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto py-2.5 px-5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-bold rounded-xl transition-all cursor-pointer"
              >
                Done
              </button>
            </div>
          </div>
        ) : isLockedByMe && secondsRemaining > 0 ? (
          /* State 1: Active 15-Minute Hold Timer Banner & Proceed to Payment Button */
          <div className="mb-6 p-5 rounded-2xl bg-[#FAF5EA] border border-[#E6C687] space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-[#785613] uppercase tracking-wider flex items-center gap-1.5">
                <Clock className="w-4 h-4 animate-spin text-[#C5A059]" />
                Hold Window Active
              </span>
              <span className="text-2xl font-mono font-bold text-[#785613]">
                {formatTimer(secondsRemaining)}
              </span>
            </div>
            <p className="text-xs text-slate-600">
              Stall {currentBooth.id} is held for your organization for 15 minutes. Proceed with online payment or request a 48-hour corporate NEFT/RTGS hold.
            </p>

            <button
              type="button"
              onClick={handleProceedToPayment}
              disabled={isPaymentLoading || offlineHoldMutation.isPending}
              className="w-full py-3.5 px-4 bg-[#0A3D27] hover:bg-[#062B1B] disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-md shadow-emerald-950/20 flex items-center justify-center gap-2 cursor-pointer text-sm"
            >
              {isPaymentLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Preparing Razorpay Checkout...</span>
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  <span>Proceed to Payment (₹{totalPayable.toLocaleString('en-IN')} incl. 18% GST)</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleRequestOffline48hHold}
              disabled={offlineHoldMutation.isPending || isPaymentLoading}
              className="w-full py-2.5 px-4 bg-white hover:bg-amber-50/70 disabled:opacity-50 text-[#785613] border border-[#E6C687] font-bold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer text-xs"
            >
              <Landmark className="w-4 h-4" />
              <span>
                {offlineHoldMutation.isPending
                  ? 'Placing 48-Hour Offline Hold...'
                  : 'Or Request 48-Hour Offline NEFT / RTGS Hold'}
              </span>
            </button>
          </div>
        ) : !isLockedByMe && isAvailable ? (
          /* State 2: Reservation Form (when available) */
          <form onSubmit={handleReserve} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Company Email <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  required
                  placeholder="exhibitor@agritech.com"
                  value={companyEmail}
                  onChange={(e) => setCompanyEmail(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Company Name <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  id="booth-company-name"
                  type="text"
                  required
                  placeholder="e.g. AgriCrop Technologies"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Contact Person <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    id="booth-contact-person"
                    type="text"
                    required
                    placeholder="Full name"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Phone <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    id="booth-contact-phone"
                    type="tel"
                    required
                    minLength={8}
                    placeholder="+91 98xxx xxxxx"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                GSTIN <span className="text-slate-400">(Optional – for a GST invoice)</span>
              </label>
              <div className="relative">
                <FileText className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  id="booth-gstin"
                  type="text"
                  maxLength={15}
                  placeholder="27AAACM1234A1Z5"
                  value={gstin}
                  onChange={(e) => setGstin(e.target.value.toUpperCase())}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm uppercase focus:outline-none focus:border-[#0A3D27] focus:bg-white transition-colors"
                />
              </div>
            </div>

            {/* Cloudflare Turnstile Bot Defense (H2) */}
            <div className="pt-1">
              <TurnstileWidget
                onVerify={handleTurnstileVerify}
                onExpire={handleTurnstileExpire}
              />
            </div>

            <div className="pt-2 space-y-2.5">
              <button
                type="submit"
                disabled={lockMutation.isPending || offlineHoldMutation.isPending}
                className="w-full py-3.5 px-4 bg-[#0A3D27] hover:bg-[#062B1B] disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-md shadow-emerald-950/20 flex items-center justify-center gap-2 cursor-pointer"
              >
                {lockMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Acquiring Atomic Lock...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    <span>Reserve for 15 Minutes (Online Payment)</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleRequestOffline48hHold}
                disabled={offlineHoldMutation.isPending || lockMutation.isPending}
                className="w-full py-2.5 px-4 bg-[#FAF5EA] hover:bg-amber-100/70 disabled:opacity-50 text-[#785613] border border-[#E6C687] font-bold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer text-xs"
              >
                <Landmark className="w-4 h-4" />
                <span>
                  {offlineHoldMutation.isPending
                    ? 'Placing 48-Hour Offline Hold...'
                    : 'Request 48-Hour Offline NEFT / RTGS Hold'}
                </span>
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 text-xs text-slate-500 pt-1">
              <ShieldCheck className="w-4 h-4 text-[#0A3D27]" />
              <span>Zero double-booking guarantee via PostgreSQL row locks</span>
            </div>
          </form>
        ) : (
          /* State 3: Booth Already Booked or On Hold by Another */
          <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl text-center space-y-3">
            <Lock className="w-8 h-8 text-amber-600 mx-auto" />
            <h4 className="text-base font-bold text-slate-900">
              {isBooked ? 'Stall Fully Booked' : 'Stall Currently on Hold'}
            </h4>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              {isBooked
                ? 'This exhibition stall has been confirmed and assigned to an exhibitor.'
                : 'Another exhibitor is currently completing reservation for this stall. If their 15-minute window expires, it will automatically become available.'}
            </p>
            <button
              onClick={onClose}
              className="mt-2 py-2 px-6 bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs font-semibold rounded-xl transition-colors cursor-pointer"
            >
              Browse Other Stalls
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoothModal;
